import { Escolha } from "../componentes/Escolha"
import { Acao, Column } from "../componentes/TablePrincipal2"
import { EstadoDoPedido, usePedido } from "../ficha/Pedido"
import { DetalheDaFicha, TabelaDaFicha, VazioDaFicha, useLinhasAbertas } from "../ficha/Tabela"
import { Input } from "../componentes/Input"
import { useState } from "react"

import {
  ErroDaApi,
  api,
  type DataEncontrada,
  type DataNaFicha,
  type DatasDoContrato as Resposta,
  type PublicidadeJuntadaNaFicha,
} from "../api"
import { Button } from "../componentes/Button"
import { CampoDeData } from "../componentes/CampoDeData"
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import { ComDica } from "../componentes/ComDica"
import * as Icones from "../componentes/Icones"
import { Numero, Numeros } from "../componentes/Numeros"
import { Selo, SeloDoValor, chaveConhecida, chaveDoNivel } from "../componentes/Selo"
import { RotuloDoCampo } from "../componentes/RotuloDoCampo"
import { data } from "../formato"
import { BOTAO_CANCELAR, GRADE_FORMULARIO, MAIUSCULA, ROTULO_DE_CAMPO } from "../lib/classes"
import { GavetaDaFicha } from "../ficha/Gaveta"
import { toast } from "../componentes/Toast"
import { cn } from "../lib/cn"

function primeiraMaiuscula(texto: string): string {
  return texto.replace(/^\p{L}/u, (letra) => letra.toLocaleUpperCase("pt-BR"))
}

function mensagem(falha: unknown): string {
  if (falha instanceof ErroDaApi) return falha.acao !== "" ? `${falha.regra}. ${falha.acao}` : falha.regra
  return falha instanceof Error ? falha.message : "falha"
}

type Props = {
  contratoId: number
  aoAbrirProcesso: (pecaId?: number) => void
  aoMudarDatas?: () => void
}

const FATO = "min-w-0 border-l border-(--borda) px-3.5 first:border-l-0 first:pl-0 [&>dt]:text-(length:--texto-xs) [&>dt]:text-muted-foreground [&>dd]:mt-1 [&>dd]:mb-0 [&>dd]:text-(length:--texto-sm) [&>dd]:font-semibold [&>dd]:text-foreground"
const PENDENCIAS = "m-0 flex list-none flex-col rounded-(--raio-controle) border border-(--borda) p-0 [&_li]:flex [&_li]:items-center [&_li]:gap-2 [&_li]:px-3 [&_li]:py-2 [&_li]:text-(length:--texto-sm) [&_li]:text-foreground [&_li:not(:last-child)]:border-b [&_li>svg]:flex-none [&_li>svg]:text-(--color-status-atencao)" // anti-ia-ok: token de cor de status, não texto de tela

const DECLARAVEIS = new Set(["vigencia_inicio", "vigencia_fim"])

type PecaParaEscolher = { id: number; rotulo: string }

export function DatasDoContrato({ contratoId, aoAbrirProcesso, aoMudarDatas }: Props) {
  const pedido = usePedido(() => api.datasDoContrato(contratoId), `datas-${contratoId}`)
  const linhasAbertas = useLinhasAbertas<string>()
  const [aberta, definirAberta] = useState<{ chave: string; ato: "corrigir" | "retirar" } | null>(null)
  const [aviso, definirAviso] = useState<string | null>(null)
  const [pecas, definirPecas] = useState<PecaParaEscolher[] | null>(null)

  const abrir = (chave: string, ato: "corrigir" | "retirar") => {
    definirAviso(null)
    definirAberta({ chave, ato })
    if (ato === "corrigir" && pecas === null) {
      api
        .processo(contratoId)
        .then((processo) =>
          definirPecas(
            processo.entradas
              .filter((e) => e.tipo_entrada === "peca" && !e.cancelada)
              .map((e) => ({
                id: e.id,
                rotulo: [e.tipo_descricao ?? e.titulo ?? "peça", e.numero, data(e.data)]
                  .filter((parte) => parte !== null && parte !== "")
                  .join(" · "),
              })),
          ),
        )
        .catch(() => definirPecas([]))
    }
  }

  const concluir = (corpo: Resposta) => {
    pedido.definir(corpo)
    definirAberta(null)
    definirAviso(corpo.aviso)
    aoMudarDatas?.()
  }

  if (pedido.dados === null) {
    return (
      <Moldura>
        <EstadoDoPedido pedido={pedido} oQue="as datas do contrato">
          {() => null}
        </EstadoDoPedido>
      </Moldura>
    )
  }
  const estado = pedido.dados

  const origem = estado.origem_do_prazo
  const linhaDaGaveta = aberta === null ? undefined : estado.datas.find((d) => d.chave === aberta.chave)
  const porChave = new Map(estado.datas.map((d) => [d.chave, d]))

  return (
    <Moldura
      selos={
        <div className="flex flex-wrap items-center gap-2">
          {origem.nivel !== null && (
            <Selo situacao={chaveDoNivel(origem.nivel)} titulo={origem.nivel_rotulo ?? undefined}>
              {primeiraMaiuscula((origem.nivel_rotulo ?? "").split(" — ")[0] ?? "")}
            </Selo>
          )}
          {origem.estado !== null && <SeloDoValor valor={origem.estado} />}
        </div>
      }
    >
      {aviso !== null && (
        <p className="mt-[5px] text-(length:--texto-sm) leading-(--linha-densa)">
          <Icones.Ressalva /> {primeiraMaiuscula(aviso)}
        </p>
      )}
      <TabelaDaFicha<DataNaFicha>
        dados={estado.datas}
        vazio={<VazioDaFicha titulo="Nenhuma data registrada para este contrato" />}
        aoClicarNaLinha={(linha) => linhasAbertas.alternar(linha.chave)}
        linhaAberta={(linha) => linhasAbertas.aberta(linha.chave)}
        detalheDaLinha={(linha) => (
          <DetalheDaData
            linha={linha}
            declaravel={DECLARAVEIS.has(linha.chave)}
            aoAbrirProcesso={aoAbrirProcesso}
            clausula={linha.chave === "vigencia_fim" ? (origem.clausula_sem_prazo ?? null) : null}
            juntada={linha.chave === "publicacao" ? estado.publicidade_juntada : null}
          />
        )}
      >
        <Column<DataNaFicha> name="rotulo" label="Data" render={(_v, linha) => <span className="font-medium">{linha.rotulo}</span>} />
        <Column<DataNaFicha> name="data" label="Quando" render={(_v, linha) => <span className="tabular-nums">{data(linha.data)}</span>} />
        <Column<DataNaFicha>
          name="estado"
          label="Situação"
          render={(_v, linha) => (
            <span className="flex flex-wrap items-center gap-1.5">
              <Selo situacao={chaveConhecida(linha.estado, "ausente")} />
              {linha.declaracao !== null && <span className="text-xs font-semibold text-primary">Vale esta</span>}
            </span>
          )}
        />
        <Column<DataNaFicha>
          name="peca_id"
          label="Prova"
          render={(_v, linha) =>
            linha.peca_id !== null ? "Peça no processo" : <span className="text-muted-foreground">Sem peça</span>
          }
        />
        <Acao<DataNaFicha>
          label="Conferir no processo"
          icon={Icones.Abrir}
          visivel={(linha) => linha.peca_id !== null}
          onClick={(linha) => aoAbrirProcesso(linha.peca_id ?? undefined)}
        />
        <Acao<DataNaFicha>
          label="Corrigir data"
          icon={Icones.Anotacao}
          visivel={(linha) => estado.pode_corrigir && DECLARAVEIS.has(linha.chave)}
          onClick={(linha) => abrir(linha.chave, "corrigir")}
        />
        <Acao<DataNaFicha>
          label="Retirar a declaração"
          icon={Icones.Dispensar}
          visivel={(linha) => estado.pode_corrigir && DECLARAVEIS.has(linha.chave) && linha.declaracao !== null}
          onClick={(linha) => abrir(linha.chave, "retirar")}
        />
      </TabelaDaFicha>

      <GavetaDaFicha
        aberta={linhaDaGaveta !== undefined}
        titulo={
          linhaDaGaveta === undefined
            ? "Data do contrato"
            : `${aberta?.ato === "retirar" ? "Retirar a declaração" : "Corrigir data"}: ${linhaDaGaveta.rotulo}`
        }
        aoFechar={() => definirAberta(null)}
      >
        {linhaDaGaveta !== undefined && aberta !== null && (
          <FormularioDaData
            key={`${aberta.chave}-${aberta.ato}`}
            contratoId={contratoId}
            linha={linhaDaGaveta}
            ato={aberta.ato}
            pecas={pecas}
            aoCancelar={() => definirAberta(null)}
            aoConcluir={(corpo) => {
              toast.success(aberta.ato === "retirar" ? "Declaração retirada" : "Data corrigida", { description: linhaDaGaveta.rotulo })
              concluir(corpo)
            }}
          />
        )}
      </GavetaDaFicha>

      <section className="mt-4 [&>h2]:mb-3 [&>h3]:mb-3">
        <h2>A contagem do prazo</h2>
        <Numeros>
          <Numero
            rotulo="Elaboração"
            valor={data(porChave.get("elaboracao")?.data ?? null)}
            nota="A data que o instrumento ostenta"
          />
          <Numero
            rotulo="Assinatura"
            valor={data(porChave.get("assinatura")?.data ?? null)}
            nota="Quando as partes assinaram"
          />
          <Numero
            rotulo={origem.marco_rotulo === null ? "Marco inicial" : `Marco: ${origem.marco_rotulo}`}
            valor={data(origem.data_do_marco)}
            nota={
              origem.marco_declarado_por !== null
                ? `Declarada por ${origem.marco_declarado_por}`
                : origem.data_do_marco === null
                  ? "É daqui que a contagem deveria começar"
                  : "É daqui que a contagem começa"
            }
            destaque
          />
          <Numero
            rotulo="Término"
            valor={data(origem.termino_juridico ?? origem.data_preventiva)}
            nota={
              origem.termino_declarado_por !== null
                ? `Declarado por ${origem.termino_declarado_por}`
                : origem.termino_juridico !== null
                  ? "Término jurídico"
                  : origem.data_preventiva !== null
                    ? "Data preventiva — não é término reconhecido"
                    : "Ainda não há término"
            }
          />
        </Numeros>
      </section>

      <section className="mt-4 [&>h2]:mb-3 [&>h3]:mb-3 rounded-(--raio-controle) border border-(--borda) bg-(--fundo) p-4">
        <h3>Por que o prazo é esse</h3>
        <dl className="grid grid-cols-[repeat(auto-fit,minmax(min(220px,100%),1fr))]">
          <div className={FATO}>
            <dt>A contagem começa</dt>
            <dd>
              {origem.marco === "data_expressa"
                ? "Pela data escrita no contrato"
                : origem.marco_rotulo === null
                  ? "Ainda não identificado"
                  : `Na ${origem.marco_rotulo}`}
              {origem.data_do_marco !== null && ` · ${data(origem.data_do_marco)}`}
            </dd>
          </div>
          <div className={FATO}>
            <dt>Prazo</dt>
            <dd>
              {origem.prazo !== null ? (
                <ComDica dica={origem.prazo.fonte}>
                  {origem.prazo.quantidade} {origem.prazo.unidade}
                </ComDica>
              ) : origem.marco === "data_expressa" ? (
                "Não há prazo a contar"
              ) : (
                "—"
              )}
            </dd>
          </div>
          <div className={FATO}>
            <dt>Fundamento</dt>
            <dd>
              <ComDica dica={origem.regra_rotulo}>
                {primeiraMaiuscula(origem.por_que_este_marco ?? "—")}
              </ComDica>
            </dd>
          </div>
        </dl>
        <details className="mt-3 text-(length:--texto-sm)">
          <summary className="cursor-pointer text-(length:--texto-xs) font-semibold text-primary hover:underline">Ver a explicação completa</summary>
          <p className={cn("mt-1.5 rounded-(--raio-controle) border border-(--borda) bg-(--branco) p-3 leading-(--linha-densa) text-muted-foreground", MAIUSCULA)}>{origem.narrativa}</p>
        </details>
      </section>

      {origem.problemas.length > 0 && (
        <section className="mt-4 [&>h2]:mb-3 [&>h3]:mb-3">
          <h3 className="flex items-center gap-1 text-(--vermelho)">
            <Icones.Ressalva /> Problemas nas datas
          </h3>
          <ul className={cn(PENDENCIAS, "[&_li]:border-(--vermelho) [&_li>svg]:text-(--vermelho)")}>
            {origem.problemas.map((problema) => {
              const [titulo, ...resto] = problema.split(": ")
              const detalhe = resto.join(": ")
              return (
                <li key={problema}>
                  <Icones.Ressalva />
                  <ComDica dica={detalhe === "" ? null : primeiraMaiuscula(detalhe)}>
                    {primeiraMaiuscula(titulo ?? "")}
                  </ComDica>
                </li>
              )
            })}
          </ul>
        </section>
      )}

      {origem.o_que_falta.length > 0 && (
        <section className="mt-4 [&>h2]:mb-3 [&>h3]:mb-3">
          <h3 className="flex items-center gap-1">Alertas</h3>
          <ul className={PENDENCIAS}>
            {origem.o_que_falta.map((item) => {
              const [titulo, ...resto] = item.split(": ")
              const detalhe = resto.join(": ")
              return (
                <li key={item}>
                  <Icones.Pendente />
                  <ComDica dica={detalhe === "" ? null : primeiraMaiuscula(detalhe)}>
                    {primeiraMaiuscula(titulo ?? "")}
                  </ComDica>
                </li>
              )
            })}
          </ul>
        </section>
      )}
    </Moldura>
  )
}

function Moldura({ children, selos }: { children: React.ReactNode; selos?: React.ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Datas do contrato e fundamento do prazo</CardTitle>
        {selos !== undefined && <CardAction>{selos}</CardAction>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  )
}

function DetalheDaData({
  linha,
  declaravel,
  aoAbrirProcesso,
  clausula,
  juntada,
}: {
  linha: DataNaFicha
  declaravel: boolean
  aoAbrirProcesso: (pecaId?: number) => void
  clausula: { trecho: string; pagina: number | null } | null
  juntada: PublicidadeJuntadaNaFicha | null
}) {
  const declaracao = linha.declaracao
  const outras = linha.encontradas.filter((achada) => declaracao !== null || !achada.em_uso)
  return (
    <div className="flex flex-col gap-3">
      {declaracao !== null ? (
        <DetalheDaFicha rotulo="Origem">
          {`Vale esta: declarada por ${declaracao.quem}`}
          {declaracao.motivo !== null ? `. Motivo: ${declaracao.motivo}` : ". Declarada sem motivo escrito"}
          {declaracao.divergencia_data !== null &&
            `. O sistema encontrou depois ${data(declaracao.divergencia_data)}${declaracao.divergencia_fonte !== null ? ` (${declaracao.divergencia_fonte})` : ""}`}
        </DetalheDaFicha>
      ) : (
        <DetalheDaFicha rotulo="Origem">
          {declaravel && linha.data !== null && linha.estado !== "validada" ? "Proposta pelo sistema · " : ""}
          {primeiraMaiuscula(linha.fonte ?? linha.motivo_da_ausencia ?? "—")}
          {linha.veiculo !== null && ` · Veículo: ${linha.veiculo}${linha.referencia !== null ? ` · ${linha.referencia}` : ""}`}
          {linha.evidencia !== null && `. ${linha.evidencia}`}
        </DetalheDaFicha>
      )}
      {outras.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">Outras datas encontradas</span>
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {outras.map((achada) => (
              <EncontradaNoDetalhe
                key={`${linha.chave}-${achada.data}`}
                achada={achada}
                declarada={declaracao !== null}
                aoAbrirProcesso={aoAbrirProcesso}
              />
            ))}
          </ul>
        </div>
      )}
      {clausula !== null && <LinhaDaClausulaSemPrazo clausula={clausula} />}
      {juntada !== null && <JuntadaNoDetalhe juntada={juntada} aoAbrirProcesso={aoAbrirProcesso} />}
    </div>
  )
}

function EncontradaNoDetalhe({
  achada,
  declarada,
  aoAbrirProcesso,
}: {
  achada: DataEncontrada
  declarada: boolean
  aoAbrirProcesso: (pecaId?: number) => void
}) {
  return (
    <li className="flex flex-wrap items-center gap-2 text-sm">
      <span className="tabular-nums">{data(achada.data)}</span>
      <span className="text-muted-foreground">
        {achada.origens.join(" · ")}
        {declarada && achada.em_uso && " · Era a proposta do sistema"}
      </span>
      {achada.peca_id !== null && (
        <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={() => aoAbrirProcesso(achada.peca_id ?? undefined)}>
          <Icones.Abrir />
          Conferir no processo
        </Button>
      )}
    </li>
  )
}

export function FormularioDaData({
  contratoId,
  linha,
  ato,
  pecas,
  aoCancelar,
  aoConcluir,
}: {
  contratoId: number
  linha: DataNaFicha
  ato: "corrigir" | "retirar"
  pecas: PecaParaEscolher[] | null
  aoCancelar: () => void
  aoConcluir: (corpo: Resposta) => void
}) {
  const [quando, definirQuando] = useState(linha.data ?? "")
  const [motivo, definirMotivo] = useState("")
  const [pecaId, definirPecaId] = useState("")
  const [ocupado, definirOcupado] = useState(false)
  const [falha, definirFalha] = useState<string | null>(null)
  const rotulo = linha.rotulo.toLocaleLowerCase("pt-BR")

  const salvar = async () => {
    definirOcupado(true)
    definirFalha(null)
    try {
      const texto = motivo.trim() === "" ? null : motivo.trim()
      aoConcluir(
        ato === "retirar"
          ? await api.retirarData(contratoId, linha.chave, texto)
          : await api.declararData(contratoId, linha.chave, {
              data: quando,
              motivo: texto,
              peca_id: pecaId === "" ? null : Number(pecaId),
            }),
      )
    } catch (erro: unknown) {
      definirFalha(mensagem(erro))
    } finally {
      definirOcupado(false)
    }
  }

  return (
    <div data-slot="formulario-da-data" className="flex flex-col">
        <div className={GRADE_FORMULARIO}>
          {ato === "corrigir" ? (
            <>
              <div>
                <RotuloDoCampo htmlFor={`declarar-${linha.chave}`} obrigatorio>
                  Data correta de {rotulo}
                </RotuloDoCampo>
                <CampoDeData
                  id={`declarar-${linha.chave}`}
                  obrigatorio
                  valor={quando}
                  aoMudar={(valor) => definirQuando(valor)}
                />
              </div>
              <div>
                <label className={ROTULO_DE_CAMPO} htmlFor={`peca-${linha.chave}`}>
                  Peça que comprova
                </label>
                <Escolha
                  id={`peca-${linha.chave}`}
                  valor={pecaId}
                  aoMudar={definirPecaId}
                  vazio={
                    pecas === null
                      ? "Buscando as peças do processo"
                      : pecas.length === 0
                        ? "O processo deste contrato não tem peças"
                        : "Nenhuma"
                  }
                  placeholderDaBusca="Buscar pelo nome da peça"
                  opcoes={(pecas ?? []).map((peca) => ({ valor: String(peca.id), rotulo: peca.rotulo }))}
                />
              </div>
            </>
          ) : null}
          <div className="col-span-full">
            <label className={ROTULO_DE_CAMPO} htmlFor={`motivo-${linha.chave}`}>
              {ato === "corrigir" ? "Por que as outras datas estão erradas" : "Por que retirar"}
            </label>
            <Input
              id={`motivo-${linha.chave}`}
              value={motivo}
              onChange={(e) => definirMotivo(e.target.value)}
            />
          </div>
          {falha !== null && <p className={cn("col-span-full mt-[5px] text-(length:--texto-sm) leading-(--linha-densa)", MAIUSCULA)}>{falha}</p>}
          <div className="col-span-full flex flex-wrap justify-end gap-2 border-t pt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={BOTAO_CANCELAR}
              disabled={ocupado}
              onClick={aoCancelar}
            >
              <Icones.Fechar />
              Cancelar
            </Button>
            <Button
              title={
                ato === "corrigir"
                  ? quando === ""
                    ? `Falta a data correta de ${rotulo}`
                    : undefined
                  : undefined
              }
              type="button"
              size="sm"
              disabled={ocupado || (ato === "corrigir" && quando === "")}
              onClick={() => void salvar()}
            >
              {ato === "corrigir" ? <Icones.Salvar /> : <Icones.Validar />}
              {ato === "corrigir" ? "Salvar" : "Retirar a declaração"}
            </Button>
          </div>
        </div>
    </div>
  )
}

export function LinhaDaClausulaSemPrazo({
  clausula,
}: {
  clausula: { trecho: string; pagina: number | null }
}) {
  return (
    <div data-slot="clausula-sem-prazo" className="text-sm">
      <span className="block font-semibold text-foreground">
        O contrato não traz data de fim: declare-a com base na cláusula
        {clausula.pagina !== null ? ` (p. ${clausula.pagina})` : ""}
      </span>
      <span className="mt-0.5 block text-muted-foreground italic first-letter:uppercase">“{clausula.trecho}”</span>
    </div>
  )
}

function JuntadaNoDetalhe({
  juntada,
  aoAbrirProcesso,
}: {
  juntada: PublicidadeJuntadaNaFicha
  aoAbrirProcesso: (pecaId?: number) => void
}) {
  return (
    <div className="flex flex-col gap-1 text-sm">
      <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">Peça de publicidade juntada</span>
      <span>
        {data(juntada.quando)} · {juntada.documento_id === null ? "Peça do processo" : `Peça ${juntada.documento_id} do ATHUS`}
        {juntada.tipo !== null && ` (${juntada.tipo})`}
      </span>
      <span className="text-xs text-muted-foreground">
        Data em que a peça entrou no processo do ATHUS. É tramitação, não é a data do diário
        {juntada.quantas > 1 && ` · O processo tem ${juntada.quantas} peças de publicidade`}
      </span>
      <Button type="button" variant="link" size="sm" className="h-auto self-start p-0" onClick={() => aoAbrirProcesso(juntada.peca_id)}>
        <Icones.Abrir />
        Conferir no processo
      </Button>
    </div>
  )
}
